/**
 * The single visual contract for "imported/edited PDF document content" — used to style
 * BOTH the live Quill editor (Settings panel, PDF preview) and the actual generated PDF
 * (Puppeteer, functions/src/core/utils/pdf.utils.ts).
 *
 * Without this, the two render from entirely disconnected style systems: Quill's on-screen
 * appearance comes from the admin app's own CSS, which never travels with the raw
 * artifactHtml string sent to Puppeteer — so the PDF silently falls back to browser
 * defaults (serif font, no table borders, images at native pixel size). Wrapping content
 * in a `ql-editor`-classed container and embedding this stylesheet on both sides guarantees
 * what the admin sees while editing is what ends up in the final PDF.
 *
 * Scoped under `.ql-editor` deliberately — that's the class Quill's own editor already
 * gives its content area, so these rules apply there with zero wiring; the PDF side gets
 * the same rules by wrapping its HTML in a `<div class="ql-editor">` before rendering.
 */
export const DOCUMENT_CONTENT_CSS = `
.ql-editor {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    font-size: 14px;
    line-height: 1.6;
    color: #1f2937;
    /* Matches the PDF's actual printable content width (A4 210mm minus 15mm side margins —
       see generatePdfFromHtml's page.pdf() margins in pdf.utils.ts). The image-resize module
       offers percentage-based sizing (e.g. "50%"), which is relative to whatever container
       it's in — without this, 50% of a narrower editor pane looks smaller on screen than the
       same 50% computed against the PDF's wider page, so a resized image would appear to
       "snap back" to a different size once actually printed. Capping both contexts to the
       same physical width makes any percentage-relative sizing (images, tables) match exactly. */
    max-width: 180mm;
    /* Without this, a container wider than 180mm (most screen contexts — the PDF's own page
       is the one place that's an exact fit) left-aligns the content block by default, piling
       all the extra width onto the right margin only. Centering keeps both sides even. */
    margin-left: auto;
    margin-right: auto;
}
.ql-editor h1 { font-size: 28px; font-weight: 700; margin: 24px 0 12px; line-height: 1.25; }
.ql-editor h2 { font-size: 22px; font-weight: 700; margin: 20px 0 10px; line-height: 1.3; }
.ql-editor h3 { font-size: 18px; font-weight: 600; margin: 16px 0 8px; line-height: 1.3; }
.ql-editor p { margin: 0 0 12px; }
.ql-editor ul, .ql-editor ol { margin: 0 0 12px; padding-left: 1.5em; }
.ql-editor li { margin-bottom: 4px; }
.ql-editor blockquote { margin: 0 0 12px; padding-left: 16px; border-left: 4px solid #d1d5db; color: #4b5563; }
.ql-editor a { color: #2563eb; text-decoration: underline; }
.ql-editor strong { font-weight: 700; }
.ql-editor em { font-style: italic; }
.ql-editor table { border-collapse: collapse; margin: 0 0 12px; width: 100%; }
.ql-editor td { border: 1px solid #333; padding: 4px 8px; vertical-align: top; }
.ql-editor img { max-width: 100%; height: auto; }
/* Alignment + text color — Quill applies these as ql-align-* classes / inline style="color"
   respectively while editing; the class-based one needs the same rule defined here since
   Puppeteer never loads Quill's own snow.css. Color is inline so it needs no rule at all. */
.ql-editor .ql-align-center { text-align: center; }
.ql-editor .ql-align-right { text-align: right; }
.ql-editor .ql-align-justify { text-align: justify; }

/* Page break marker (see settings-panel.component.ts's PageBreakBlot) — visible as a
   labeled dashed divider while editing, so the admin can see and delete it like any
   other block; invisible in the actual PDF (Puppeteer renders under "print" media by
   default) where only its pagination effect should show, never the editing affordance
   itself. */
.ql-editor .ql-page-break {
    margin: 20px 0;
    padding-top: 6px;
    border-top: 1px dashed #9ca3af;
    text-align: center;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #9ca3af;
    user-select: none;
}
.ql-editor .ql-page-break::after { content: 'Page break'; }
@media print {
    .ql-editor .ql-page-break {
        margin: 0; padding: 0; border: none; height: 0; line-height: 0; font-size: 0;
        break-after: page;
        page-break-after: always;
    }
    .ql-editor .ql-page-break::after { content: ''; }
}
`;
